import {
  Box,
  H3,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@ouster/webviz-react-components';

import { StatisticBox, StatisticGroup } from './StatisticGroup';
import {
  useAdminDashboardSummary,
  useAdminDashboardSummaryByRange,
} from './useAdminDashboard';
import { useStoreActions, useStoreState } from '../../reducers';
import { formatBytes, formatNumber } from '../../utils/misc';

const rangeOptions = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

const LatestStaticBox = () => {
  const { summaryTimeRange } = useStoreState(
    (storeState) => storeState.adminDashboard
  );
  const { setSummaryTimeRange } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  const handleTimeRangeChange = (range: number) => {
    setSummaryTimeRange(range);
  };

  const { data: rangeSummary, isLoading: isRangeSummaryLoading } =
    useAdminDashboardSummaryByRange(summaryTimeRange);

  return (
    <Box
      p="1.5rem"
      border="1px solid var(--color-unify-white-40)"
      borderRadius={8}
      flexGrow={1}
      data-testid="admin-overall-summary-latest-box"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={8}
      >
        <H3 fontSize={18}>Last {summaryTimeRange} days</H3>
        <Menu placement="bottom-end" offset={[5, 0]}>
          <MenuButton data-testid="admin-overall-summary-range-btn">
            <Icon
              icon="settings"
              color="var(--ouster-colors-gray-400)"
              _hover={{ color: 'white' }}
            />
          </MenuButton>
          <MenuList data-testid="admin-overall-summary-range-list">
            {rangeOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleTimeRangeChange(option.value)}
                style={{
                  fontSize: 14,
                  paddingBlock: '0.75rem',
                  paddingInline: '1.25rem',
                  marginBlock: '0.25rem',
                  columnGap: '0.5rem',
                }}
                _hover={{ bgColor: 'var(--ouster-colors-gray-700)' }}
                data-testid={`admin-overall-summary-range-item-${option.value}`}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Box>
      <Box display="flex" columnGap="1rem">
        <>
          <StatisticBox
            label="Organization created"
            loading={isRangeSummaryLoading}
            value={formatNumber(rangeSummary?.total_organizations || 0)}
          />
          <StatisticBox
            label="User joined"
            loading={isRangeSummaryLoading}
            value={formatNumber(rangeSummary?.total_user_count || 0)}
          />
        </>
      </Box>
    </Box>
  );
};

export default function AdminOverallSummary() {
  const { data: summaryData, isLoading: summaryLoading } =
    useAdminDashboardSummary();

  return (
    <Box display="flex" columnGap={8} data-testid="admin-overall-summary-box">
      <StatisticGroup
        title="Today so far"
        stats={[
          {
            label: 'Total Users',
            value: formatNumber(summaryData?.total_user_count),
            loading: summaryLoading,
          },
          {
            label: 'Total Organizations',
            value: formatNumber(summaryData?.total_organizations),
            loading: summaryLoading,
          },
          {
            label: 'Total Used Storage',
            value: formatBytes(summaryData?.total_storage),
            loading: summaryLoading,
          },
        ]}
        data-testid="admin-overall-summary-group"
      />
      <LatestStaticBox />
    </Box>
  );
}
